/**
 * Shared schema: Drizzle tables (SQLite) + Zod API schemas + domain TS types.
 *
 * Scope covers Stage 0 MVP **and** Stage 0.5 Screenshot Feature Parity:
 *   - savedPanels, queryCache, queryRuns  (Stage 0)
 *   - decisions                           (DEC-01)
 *   - PathologyPreset                     (PATH-01..03)
 *   - UncertaintyLevel / ExplanationRecord / PartnerProteinDetail
 *   - JSON export envelope
 *   - Forward-compatible fields for REPR-01..03 (snapshot metadata) so we don't
 *     need a schema rewrite when Stage 1 snapshot replay lands.
 *
 * v1 entities (Workspaces, Projects, Folders, Snapshots, Annotations,
 * ComparisonSets, ExportJobs, AssistantSession) are intentionally not yet
 * materialised here.
 */

import { sql } from "drizzle-orm";
import { integer, sqliteTable, text } from "drizzle-orm/sqlite-core";
import { z } from "zod";

/* ───────────────────────── Evidence tiers ───────────────────────── */

export const EVIDENCE_TIERS = [
  "experimental",
  "curated_db",
  "ai_structural_high",
  "ai_structural_low",
  "cross_species_inferred",
  "textmining",
  "coexpression",
  "neighborhood",
  "weak_conflicting",
] as const;

export const EvidenceTierSchema = z.enum(EVIDENCE_TIERS);
export type EvidenceTier = z.infer<typeof EvidenceTierSchema>;

/* ───────────────────────── Uncertainty level (UNC-01) ───────────────────────── */

export const UNCERTAINTY_LEVELS = ["low", "moderate", "high"] as const;
export const UncertaintyLevelSchema = z.enum(UNCERTAINTY_LEVELS);
export type UncertaintyLevel = z.infer<typeof UncertaintyLevelSchema>;

/* ───────────────────────── Species ───────────────────────── */

export const SPECIES = {
  human: { taxId: 9606, label: "Homo sapiens", common: "Human" },
  mouse: { taxId: 10090, label: "Mus musculus", common: "Mouse" },
  rat: { taxId: 10116, label: "Rattus norvegicus", common: "Rat" },
  zebrafish: { taxId: 7955, label: "Danio rerio", common: "Zebrafish" },
  drosophila: { taxId: 7227, label: "Drosophila melanogaster", common: "Fruit fly" },
  c_elegans: { taxId: 6239, label: "Caenorhabditis elegans", common: "C. elegans" },
  yeast: { taxId: 4932, label: "Saccharomyces cerevisiae", common: "Yeast" },
  arabidopsis: { taxId: 3702, label: "Arabidopsis thaliana", common: "A. thaliana" },
} as const;

export type SpeciesKey = keyof typeof SPECIES;
export const SpeciesKeySchema = z.enum(
  Object.keys(SPECIES) as [SpeciesKey, ...SpeciesKey[]],
);

/* ───────────────────────── Drizzle tables ───────────────────────── */

export const queryCache = sqliteTable("query_cache", {
  key: text("key").primaryKey(),
  source: text("source").notNull(),
  payload: text("payload").notNull(),
  createdAt: integer("created_at", { mode: "timestamp_ms" })
    .notNull()
    .default(sql`(unixepoch() * 1000)`),
  expiresAt: integer("expires_at", { mode: "timestamp_ms" }).notNull(),
});

export const savedPanels = sqliteTable("saved_panels", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  name: text("name").notNull(),
  genes: text("genes").notNull(),
  species: text("species").notNull(),
  notes: text("notes"),
  createdAt: integer("created_at", { mode: "timestamp_ms" })
    .notNull()
    .default(sql`(unixepoch() * 1000)`),
  updatedAt: integer("updated_at", { mode: "timestamp_ms" })
    .notNull()
    .default(sql`(unixepoch() * 1000)`),
});

export const queryRuns = sqliteTable("query_runs", {
  id: text("id").primaryKey(),
  queryText: text("query_text").notNull(),
  speciesKey: text("species_key").notNull(),
  contextPreset: text("context_preset"),
  filtersJson: text("filters_json").notNull(),
  confidenceThreshold: integer("confidence_threshold").notNull(),
  results: text("results"),
  sourceTimings: text("source_timings"),
  appVersion: text("app_version").notNull(),
  kbVersion: text("kb_version").notNull(),
  scoringConfigVersion: text("scoring_config_version").notNull(),
  createdAt: integer("created_at", { mode: "timestamp_ms" })
    .notNull()
    .default(sql`(unixepoch() * 1000)`),
  completedAt: integer("completed_at", { mode: "timestamp_ms" }),
});

/** Decision log (DEC-01). One row per action on an interaction. A single
 *  (interactionId, deviceId) pair is allowed to hold multiple historical rows
 *  so we can later derive an audit trail; the *current* state is the most
 *  recent row (per `created_at`). Reason is optional.
 */
export const decisions = sqliteTable("decisions", {
  id: text("id").primaryKey(),
  interactionId: text("interaction_id").notNull(),
  sourceGene: text("source_gene").notNull(),
  partnerGene: text("partner_gene").notNull(),
  speciesKey: text("species_key").notNull(),
  action: text("action").notNull(),
  reason: text("reason"),
  queryRunId: text("query_run_id"),
  deviceId: text("device_id").notNull(),
  appVersion: text("app_version").notNull(),
  kbVersion: text("kb_version").notNull(),
  createdAt: integer("created_at", { mode: "timestamp_ms" })
    .notNull()
    .default(sql`(unixepoch() * 1000)`),
});

export type SavedPanel = typeof savedPanels.$inferSelect;
export type InsertSavedPanel = typeof savedPanels.$inferInsert;
export type QueryRunRow = typeof queryRuns.$inferSelect;
export type InsertQueryRun = typeof queryRuns.$inferInsert;
export type QueryCacheRow = typeof queryCache.$inferSelect;
export type InsertQueryCache = typeof queryCache.$inferInsert;
export type DecisionRow = typeof decisions.$inferSelect;
export type InsertDecision = typeof decisions.$inferInsert;

/* ───────────────────────── Domain DTOs ───────────────────────── */

export const ProteinInfoSchema = z.object({
  uniprotId: z.string(),
  geneName: z.string(),
  proteinName: z.string().optional(),
  function: z.string().optional(),
  organism: z.string().optional(),
  reviewed: z.boolean().optional(),
  sequenceLength: z.number().optional(),
});
export type ProteinInfo = z.infer<typeof ProteinInfoSchema>;

export const DiseaseAssociationSchema = z.object({
  diseaseId: z.string(),
  diseaseName: z.string(),
  score: z.number().min(0).max(1),
});
export type DiseaseAssociation = z.infer<typeof DiseaseAssociationSchema>;

export const AlphaFoldInfoSchema = z.object({
  uniprotId: z.string(),
  averagePlddt: z.number().optional(),
  modelUrl: z.string().optional(),
});
export type AlphaFoldInfo = z.infer<typeof AlphaFoldInfoSchema>;

/** Per-channel STRING evidence (raw 0–1 floats, not the 0–1000 display scale). */
export const StringChannelsSchema = z.object({
  combined: z.number().min(0).max(1),
  experimental: z.number().min(0).max(1).optional(),
  database: z.number().min(0).max(1).optional(),
  textmining: z.number().min(0).max(1).optional(),
  coexpression: z.number().min(0).max(1).optional(),
  neighborhood: z.number().min(0).max(1).optional(),
  fusion: z.number().min(0).max(1).optional(),
  cooccurrence: z.number().min(0).max(1).optional(),
});
export type StringChannels = z.infer<typeof StringChannelsSchema>;

export const LabPriorityFactorsSchema = z.object({
  experimental: z.number().min(0).max(1),
  aiConfidence: z.number().min(0).max(1),
  diseaseRelevance: z.number().min(0).max(1),
  novelty: z.number().min(0).max(1),
  tractability: z.number().min(0).max(1),
  conservation: z.number().min(0).max(1),
});
export type LabPriorityFactors = z.infer<typeof LabPriorityFactorsSchema>;

/** Programmatic explanation record (EXPL-01..05; shipping Stage 0.5). */
export const ExplanationRecordSchema = z.object({
  headline: z.string(),
  composition: z.string().optional(),
  bullets: z.array(z.string()).default([]),
  missing: z.array(z.string()).default([]),
  upgrades: z.array(z.string()).default([]),
  assay: z
    .object({
      first: z.string(),
      orthogonal: z.string(),
      controls: z.string(),
    })
    .optional(),
});
export type ExplanationRecord = z.infer<typeof ExplanationRecordSchema>;

/** Per-species conservation detail (drawer DRW-10). */
export const ConservationHitSchema = z.object({
  species: SpeciesKeySchema,
  evidence: z.enum(["direct", "inferred", "none"]),
});
export type ConservationHit = z.infer<typeof ConservationHitSchema>;

export const InteractionCandidateSchema = z.object({
  id: z.string(),
  sourceGene: z.string(),
  sourceUniprotId: z.string().optional(),
  partnerGene: z.string(),
  partnerUniprotId: z.string().optional(),
  speciesKey: SpeciesKeySchema,
  scores: StringChannelsSchema,
  evidenceTier: EvidenceTierSchema,
  uncertainty: UncertaintyLevelSchema.default("moderate"),
  conservation: z.array(SpeciesKeySchema).default([]),
  conservationHits: z.array(ConservationHitSchema).default([]),
  diseaseAssociations: z.array(DiseaseAssociationSchema).default([]),
  alphaFold: AlphaFoldInfoSchema.nullable().optional(),
  labPriority: z
    .object({
      score: z.number().min(0).max(1),
      factors: LabPriorityFactorsSchema,
      rationale: z.array(z.string()).default([]),
      caveats: z.array(z.string()).default([]),
      suggestedPrimaryAssay: z.string().optional(),
      suggestedOrthogonalAssay: z.string().optional(),
      suggestedControls: z.string().optional(),
    })
    .optional(),
  explanation: ExplanationRecordSchema.optional(),
});
export type InteractionCandidate = z.infer<typeof InteractionCandidateSchema>;

/* ───────────────────────── Partner Protein Detail (PPD-01..06) ───────────────────────── */

export const GoTermSchema = z.object({
  id: z.string(),
  label: z.string(),
  aspect: z.enum(["MF", "BP", "CC"]),
});
export type GoTerm = z.infer<typeof GoTermSchema>;

export const BindingSiteSchema = z.object({
  kind: z.enum(["binding_site", "active_site", "metal_binding", "site"]),
  residues: z.array(z.string()),
  description: z.string().optional(),
  source: z.string().optional(),
});
export type BindingSite = z.infer<typeof BindingSiteSchema>;

export const PartnerProteinDetailSchema = z.object({
  uniprotId: z.string(),
  geneName: z.string(),
  proteinName: z.string().optional(),
  organism: z.string().optional(),
  sequenceLength: z.number().optional(),
  function: z.string().optional(),
  subcellularLocations: z.array(z.string()).default([]),
  molecularFunctionGo: z.array(GoTermSchema).default([]),
  biologicalProcessGo: z.array(GoTermSchema).default([]),
  bindingSites: z.array(BindingSiteSchema).default([]),
  averagePlddt: z.number().optional(),
});
export type PartnerProteinDetail = z.infer<typeof PartnerProteinDetailSchema>;

/* ───────────────────────── Context (from biocontext-kb.json) ───────────────────────── */

export const ContextProfileSchema = z.object({
  tissue: z.string().optional(),
  cellType: z.string().optional(),
  repairState: z.string().optional(),
  diseaseMode: z.string().optional(),
});
export type ContextProfile = z.infer<typeof ContextProfileSchema>;

/* ───────────────────────── Filters ───────────────────────── */

export const FilterStateSchema = z.object({
  confidenceMin: z.number().min(0).max(1).default(0.4),
  evidenceTiers: z.array(EvidenceTierSchema).default([]),
  experimentalOnly: z.boolean().default(false),
  diseaseRelevantOnly: z.boolean().default(false),
  highPriorityOnly: z.boolean().default(false),
  partnerGeneSearch: z.string().default(""),
});
export type FilterState = z.infer<typeof FilterStateSchema>;

/* ───────────────────────── API envelopes ───────────────────────── */

export const AnalysisRequestSchema = z.object({
  genes: z.array(z.string().min(1)).min(1).max(200),
  species: SpeciesKeySchema,
  context: ContextProfileSchema.optional(),
  filters: FilterStateSchema.partial().optional(),
  /** If true, bypass cache read for all source calls (REF-01). */
  refresh: z.boolean().optional(),
});
export type AnalysisRequest = z.infer<typeof AnalysisRequestSchema>;

export type SourceStatus = "ok" | "degraded" | "rate_limited" | "failed";

export const SourceHealthSchema = z.object({
  source: z.enum(["string", "uniprot", "opentargets", "alphafold"]),
  status: z.enum(["ok", "degraded", "rate_limited", "failed"]),
  fetchedAt: z.number().nullable(),
  latencyMs: z.number().nullable(),
  message: z.string().optional(),
});
export type SourceHealth = z.infer<typeof SourceHealthSchema>;

export const AnalysisResponseSchema = z.object({
  queryRunId: z.string(),
  queryText: z.string(),
  species: SpeciesKeySchema,
  context: ContextProfileSchema.optional(),
  interactions: z.array(InteractionCandidateSchema),
  proteinInfo: z.record(z.string(), ProteinInfoSchema).default({}),
  partnerDetails: z.record(z.string(), PartnerProteinDetailSchema).default({}),
  sourceHealth: z.array(SourceHealthSchema),
  meta: z.object({
    appVersion: z.string(),
    kbVersion: z.string(),
    scoringConfigVersion: z.string(),
    generatedAt: z.number(),
    cacheTtlSeconds: z.number(),
  }),
});
export type AnalysisResponse = z.infer<typeof AnalysisResponseSchema>;

/* ───────────────────────── Saved panels API ───────────────────────── */

export const CreateSavedPanelSchema = z.object({
  name: z.string().min(1).max(200),
  genes: z.array(z.string().min(1)).min(1).max(500),
  species: SpeciesKeySchema,
  notes: z.string().max(2000).optional(),
});
export type CreateSavedPanelInput = z.infer<typeof CreateSavedPanelSchema>;

export const UpdateSavedPanelSchema = CreateSavedPanelSchema.partial();
export type UpdateSavedPanelInput = z.infer<typeof UpdateSavedPanelSchema>;

/* ───────────────────────── Decisions API (DEC-01..05) ───────────────────────── */

export const DECISION_ACTIONS = ["validate_now", "maybe_later", "reject"] as const;
export const DecisionActionSchema = z.enum(DECISION_ACTIONS);
export type DecisionAction = z.infer<typeof DecisionActionSchema>;

export const CreateDecisionSchema = z.object({
  interactionId: z.string().min(1),
  sourceGene: z.string().min(1),
  partnerGene: z.string().min(1),
  speciesKey: SpeciesKeySchema,
  action: DecisionActionSchema,
  reason: z.string().max(2000).optional().nullable(),
  queryRunId: z.string().optional().nullable(),
  deviceId: z.string().min(1).optional(),
});
export type CreateDecisionInput = z.infer<typeof CreateDecisionSchema>;

export const UpdateDecisionSchema = z.object({
  action: DecisionActionSchema.optional(),
  reason: z.string().max(2000).nullable().optional(),
});
export type UpdateDecisionInput = z.infer<typeof UpdateDecisionSchema>;

export const DecisionDtoSchema = z.object({
  id: z.string(),
  interactionId: z.string(),
  sourceGene: z.string(),
  partnerGene: z.string(),
  speciesKey: SpeciesKeySchema,
  action: DecisionActionSchema,
  reason: z.string().nullable().optional(),
  queryRunId: z.string().nullable().optional(),
  deviceId: z.string(),
  appVersion: z.string(),
  kbVersion: z.string(),
  createdAt: z.number(),
});
export type DecisionDto = z.infer<typeof DecisionDtoSchema>;

/* ───────────────────────── Pathology presets (PATH-01..03) ───────────────────────── */

export const PathologyPresetSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  genes: z.array(z.string()),
  species: SpeciesKeySchema.default("human"),
  suggestedTissue: z.string().optional(),
  suggestedCellType: z.string().optional(),
  suggestedDiseaseMode: z.string().optional(),
  suggestedRepairState: z.string().optional(),
});
export type PathologyPreset = z.infer<typeof PathologyPresetSchema>;

/* ───────────────────────── Comparative Analysis (COMP-07..20) ───────────────────────── */

/**
 * In-panel gene comparison output. The same `ComparisonSet` envelope is
 * forward-compatible with snapshot-diff comparisons (COMP-01..06) — the
 * `mode` discriminator and the optional `snapshotDiff` block are stubs that
 * Stage 1 fills out without an API contract change.
 *
 * Hard invariant (PRD §10.3.3): re-deriving from the same inputs yields
 * identical output.
 */

export const ComparisonModeSchema = z.enum(["in_panel", "snapshot_diff"]);
export type ComparisonMode = z.infer<typeof ComparisonModeSchema>;

/** Per-edge contribution to a Shared / Hub / Bridge entry — one row per
 *  source query gene that interacts with the partner. */
export const ComparisonEdgeSchema = z.object({
  queryGene: z.string(),
  partnerGene: z.string(),
  evidenceTier: EvidenceTierSchema,
  combinedScore: z.number().min(0).max(1),
  /** 0–1000 display scale, pre-rounded for the table. */
  combinedScore1000: z.number().min(0).max(1000),
  labPriorityScore: z.number().min(0).max(1).optional(),
  uncertainty: UncertaintyLevelSchema.optional(),
  /** The `id` of the underlying InteractionCandidate (`{source}|{partner}|{species}`),
   *  so the UI can drill into the existing detail drawer. */
  interactionId: z.string(),
});
export type ComparisonEdge = z.infer<typeof ComparisonEdgeSchema>;

/** Partner that interacts with ≥ 2 query genes (COMP-11). */
export const SharedPartnerSchema = z.object({
  partnerGene: z.string(),
  partnerUniprotId: z.string().optional(),
  queryGenes: z.array(z.string()),
  nQueryGenes: z.number().int().min(2),
  avgScore1000: z.number().min(0).max(1000),
  edges: z.array(ComparisonEdgeSchema),
});
export type SharedPartner = z.infer<typeof SharedPartnerSchema>;

/** Per-query-gene block of partners that interact with exactly one query
 *  gene (COMP-12). */
export const UniqueBlockSchema = z.object({
  queryGene: z.string(),
  count: z.number().int().min(0),
  edges: z.array(ComparisonEdgeSchema),
});
export type UniqueBlock = z.infer<typeof UniqueBlockSchema>;

/** Hub entry — subset of Shared above the configured hub threshold (COMP-13).
 *  Carries `degree` for COMP-17 promiscuity surfacing (currently sourced from
 *  STRING partner counts, populated at -1 if unavailable). */
export const HubPartnerSchema = SharedPartnerSchema.extend({
  rank: z.number().int().min(1),
  /** Total STRING degree at the active confidence floor; -1 if the source
   *  did not return a degree estimate. */
  degree: z.number().int(),
  promiscuityRisk: z.boolean().default(false),
});
export type HubPartner = z.infer<typeof HubPartnerSchema>;

/** Bridge entry — partner connecting ≥ 2 query genes that do not directly
 *  interact above the active confidence threshold (COMP-14). */
export const BridgePartnerSchema = z.object({
  rank: z.number().int().min(1),
  partnerGene: z.string(),
  partnerUniprotId: z.string().optional(),
  /** All ordered pairs of query genes the partner bridges. */
  bridgedPairs: z.array(z.tuple([z.string(), z.string()])),
  /** Flat list of every query gene that touches this partner. */
  queryGenes: z.array(z.string()),
  nQueryGenes: z.number().int().min(2),
  avgScore1000: z.number().min(0).max(1000),
  edges: z.array(ComparisonEdgeSchema),
});
export type BridgePartner = z.infer<typeof BridgePartnerSchema>;

export const ComparisonSummarySchema = z.object({
  totalInteractions: z.number().int().min(0),
  sharedCount: z.number().int().min(0),
  uniqueCount: z.number().int().min(0),
  hubsCount: z.number().int().min(0),
  bridgesCount: z.number().int().min(0),
  /** Hub threshold actually used (`ceil(0.5 × n_query_genes)` floor 2 by default). */
  hubThreshold: z.number().int().min(2),
  promiscuityDegreeFloor: z.number().int().min(0),
});
export type ComparisonSummary = z.infer<typeof ComparisonSummarySchema>;

export const ComparisonSetSchema = z.object({
  mode: ComparisonModeSchema,
  queryRunId: z.string(),
  queryGenes: z.array(z.string()),
  species: SpeciesKeySchema,
  context: ContextProfileSchema.optional(),
  summary: ComparisonSummarySchema,
  shared: z.array(SharedPartnerSchema),
  unique: z.array(UniqueBlockSchema),
  hubs: z.array(HubPartnerSchema),
  bridges: z.array(BridgePartnerSchema),
  meta: z.object({
    appVersion: z.string(),
    kbVersion: z.string(),
    scoringConfigVersion: z.string(),
    generatedAt: z.number(),
  }),
});
export type ComparisonSet = z.infer<typeof ComparisonSetSchema>;

/** Request envelope for `POST /api/analysis/compare` (COMP-19). Shape mirrors
 *  `AnalysisRequest` so the same cache key drives both endpoints. */
export const ComparisonRequestSchema = AnalysisRequestSchema.extend({
  /** Override the COMP-13 hub threshold; defaults to ceil(0.5 × n) floor 2. */
  hubThreshold: z.number().int().min(2).optional(),
  /** Override the COMP-17 promiscuity floor; default 50. */
  promiscuityDegreeFloor: z.number().int().min(0).optional(),
});
export type ComparisonRequest = z.infer<typeof ComparisonRequestSchema>;
